import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import addFamilyMember, {
  AddFamilyMember,
} from 'core/application/employeeDashboard/getReimbursed/reimbursementRequest/familyMembers/addFamilyMember'
import { FamilyMemberRelationship } from 'core/domain/companies/familyMemberRelationship'

import Input from 'common/components/dataEntry/formik/FormikDefaultInputField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Modal from 'common/components/feedback/Modal'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'

import { commandRequest } from 'common/RequestUtils'

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    firstName: Yup.string()
      .trim()
      .typeError(translate('translation:general.required'))
      .required(translate('translation:general.required')),
    lastName: Yup.string()
      .trim()
      .typeError(translate('translation:general.required'))
      .required(translate('translation:general.required')),
    familyMemberRelationship: Yup.number()
      .oneOf(
        [FamilyMemberRelationship.Spouse, FamilyMemberRelationship.Dependent],
        translate('translation:general.required'),
      )
      .typeError(translate('translation:general.required'))
      .required(translate('translation:general.required')),
  })

interface AddFamilyMemberProps {
  visible: boolean
  afterSuccessAdd: (familyMemberId: string, familyFirstName: string) => void
  onCancel: () => void
}

const AddFamilyMemberModal: React.FC<AddFamilyMemberProps> = (props) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])
  const [loading, setLoading] = useState<boolean>(false)
  const [isFormValid, setIsFormValid] = useState<boolean>(false)

  let submitFormHandler: () => Promise<any> = () => new Promise(() => {})

  const initialValues: any = {
    firstName: '',
    lastName: '',
  }

  const onSubmit = async (values: AddFamilyMember) => {
    setLoading(true)
    const result = await commandRequest(() => addFamilyMember(values))
    setLoading(false)

    if (!result.errors) {
      props.afterSuccessAdd(result, values.firstName)
    } else {
      notification.open({
        message: result.errors[0],
        type: 'error',
      })
    }
  }

  const modalFooterActions = [
    <Button key="back" onClick={props.onCancel}>
      {translate('translation:general.cancel')}
    </Button>,
    <Button key="submit" type="primary" disabled={!isFormValid} loading={loading} onClick={() => submitFormHandler()}>
      {translate('translation:general.add')}
    </Button>,
  ]

  return (
    <Modal
      title={translate('get-reimburse.request.add-new-family-memeber-title')}
      visible={props.visible}
      destroyOnClose
      closable
      onCancel={props.onCancel}
      footer={modalFooterActions}
    >
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={(values) => {
          setIsFormValid(formValidator(translate).isValidSync(values))
        }}
      >
        {(formik) => {
          const { handleSubmit, submitForm } = formik
          submitFormHandler = submitForm
          return (
            <form onSubmit={handleSubmit} autoComplete="off">
              <Row className="mb-12">
                <Col span={12} className="pr-8 ">
                  <Input label={translate('translation:general.first-name')} inputCustomStyle="mt-4" name="firstName" />
                </Col>
                <Col span={12} className="pl-8">
                  <Input label={translate('translation:general.last-name')} inputCustomStyle="mt-4" name="lastName" />
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Radio.Group
                    name="familyMemberRelationship"
                    value={formik.values.familyMemberRelationship}
                    label={translate('get-reimburse.request.relationship-label')}
                  >
                    <Row gutter={10}>
                      <Col span={12}>
                        <Radio.Button
                          value={FamilyMemberRelationship.Spouse}
                          className="custom-small-without-title radio-with-checkmark"
                        >
                          {translate('get-reimburse.request.spouse-label')}
                        </Radio.Button>
                      </Col>
                      <Col span={12}>
                        <Radio.Button
                          value={FamilyMemberRelationship.Dependent}
                          className="custom-small-without-title radio-with-checkmark"
                        >
                          {translate('get-reimburse.request.dependent-label')}
                        </Radio.Button>
                      </Col>
                    </Row>
                  </Radio.Group>
                </Col>
              </Row>
            </form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default AddFamilyMemberModal
